import Link from "next/link";

import { Icona } from "@/components/home/Icona";
import { TITOLO_LINK } from "@/components/home/stile";
import { FUOCO } from "@/components/ui/pulsanti";
import { TIPO_DERIVATO } from "@/lib/constants";
import { riassuntoRiferimenti, type RigaRullo } from "@/lib/rullo";

/** Il testo con la parola cercata in evidenza (`mark`, `.oi-rc mark` in
 *  globals.css: velo rame e filetto sotto, come nel modello). Solo a
 *  inizio di parola: «Iran» accende «Iran» e «iraniano», non «Tirana».
 *  La ricerca a monte è una sottostringa e trova anche «Tirana»; la riga
 *  resta (il conteggio è quello di monte), ma non si fa credere che la
 *  parola ci sia. */
export function ConEvidenza({ testo, q }: { testo: string; q: string }) {
  const parola = q.trim();
  if (!parola) return <>{testo}</>;
  const esc = parola.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(?<![\\p{L}\\p{N}])(${esc})`, "giu");
  const parti = testo.split(re);
  return (
    <>
      {parti.map((x, i) => (i % 2 === 1 ? <mark key={i}>{x}</mark> : <span key={i}>{x}</span>))}
    </>
  );
}

/** I rombi del filone accanto a «Report» / «Caso» (`.r-rep .mini` del
 *  modello): tre vuoti e l'ultimo pieno. */
function Rombi() {
  return (
    <svg viewBox="0 0 46 10" aria-hidden className="h-2.5 w-[46px] overflow-visible">
      <line x1="2" y1="5" x2="44" y2="5" className="stroke-mare" />
      {[5, 17, 29].map((x) => (
        <path key={x} d={`M${x} 1.5 L${x + 3.5} 5 L${x} 8.5 L${x - 3.5} 5 Z`} className="fill-bg stroke-mare" />
      ))}
      <path d="M41 1.5 L44.5 5 L41 8.5 L37.5 5 Z" className="fill-segnale stroke-segnale" />
    </svg>
  );
}

/**
 * Una riga dei risultati di /ricerca (`.r-it` del modello del 20/10/2026):
 * l'ora a sinistra, poi PAESE · CATEGORIA · tipo e a destra il codice,
 * il titolo serif 20, il sommario 15 e i riferimenti in mono. La parola
 * cercata è evidenziata in titolo e sommario. Report e casi portano il
 * filetto a sinistra (mare / rame) e i rombi con le tappe.
 *
 * Senza direttiva: la rende il server per la prima pagina e il client per
 * i blocchi aggiunti con «Mostra altri risultati».
 */
export function RigaRisultato({ r, q }: { r: RigaRullo; q: string }) {
  const filone = r.tipoDerivato && r.tipoDerivato !== "notizia" ? r.tipoDerivato : null;
  const riassunto = riassuntoRiferimenti(r.riferimenti);
  return (
    <li
      className={`oi-rc-riga grid grid-cols-[64px_minmax(0,1fr)] gap-4 border-b border-rule py-5 max-[900px]:grid-cols-[minmax(0,1fr)] max-[900px]:gap-1 ${
        filone === "report" ? "oi-rc-report" : filone === "caso" ? "oi-rc-caso" : ""
      }`}
    >
      <time dateTime={r.iso} className="pt-[3px] font-mono text-meta text-mare">
        {r.ora}
      </time>
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-meta tracking-[0.06em] text-sommesso">
          {r.paese ? (
            <>
              <Icona nome="paese" className="size-4 text-sommesso" />
              <b className="font-semibold uppercase text-ink">{r.paese}</b>
            </>
          ) : null}
          {r.categoria ? <span className="uppercase">· {r.categoria}</span> : null}
          {filone ? (
            <span className="inline-flex items-center gap-2 font-sans text-voce tracking-normal">
              <span aria-hidden className="font-mono text-sommesso">·</span>
              <Rombi />
              <span className="font-semibold text-segnale">{TIPO_DERIVATO[filone].label}</span>
              {r.tappe ? <b className="font-semibold text-mare">{r.tappe} tappe</b> : null}
            </span>
          ) : (
            <span className="font-sans text-voce font-medium tracking-normal text-sommesso">· Notizia</span>
          )}
          <span className="oi-rc-codice ml-auto text-[11px] tracking-[0.04em] max-[900px]:ml-0 max-[900px]:w-full">{r.codice}</span>
        </p>
        <h3 className="m-0 font-serif text-[20px] font-normal leading-[1.3]">
          <Link href={r.href} className={`${TITOLO_LINK} ${FUOCO}`}>
            <ConEvidenza testo={r.titolo} q={q} />
          </Link>
        </h3>
        {r.estratto ? (
          <p className="oi-rc-sommario m-0 line-clamp-3 max-w-[760px] font-sans text-[15px] leading-[1.5] text-sommesso">
            <ConEvidenza testo={r.estratto} q={q} />
          </p>
        ) : null}
        {riassunto ? <p className="oi-rc-ent m-0 mt-0.5 font-mono text-[11px] tracking-[0.04em] text-mare">{riassunto}</p> : null}
      </div>
    </li>
  );
}
